import { useEffect, useRef, useState, type FormEvent } from 'react';

import type { SelectableParty } from '@clm/shared-types';

import { ApiError } from '../api/errors';
import { classifyApiError } from '../api/problem';
import { registerVendorParty, vendorRegistrationConflict } from '../api/register-vendor-party';
import { loginRequest, msalInstance } from '../auth/msal';

/**
 * Register a vendor from its typed legal name — CLM-78 (R-76-2), IR-03 interim.
 *
 * One input, one button. The **key is never typed**: it is minted by the API
 * from the reserved `V…` space and shown back read-only (FR-14). On 201 the new
 * party is handed to the caller as a {@link SelectableParty} — the same object
 * the picker would have offered — so the surface files against a key the API
 * returned, never one it invented. On 409 `vendor_exists` the party already
 * carrying this name (by display name or active alias) is offered with *Use this
 * vendor*, because two rows for one supplier is exactly what the duplicate check
 * exists to prevent.
 *
 * Rule 11: the one request is `registerVendorParty` in `src/api`.
 */

export interface RegisterVendorFormProps {
  /** The picker's id, so the form's controls derive unique ids from it. */
  readonly id: string;
  readonly disabled?: boolean;
  /** Pre-fill from the search the user just typed, when there is one. */
  readonly initialName?: string;
  /** The party to select — the registered one, or the existing one on `vendor_exists`. */
  readonly onRegistered: (party: SelectableParty) => void;
  readonly onCancel: () => void;
}

type Phase =
  | { readonly kind: 'idle' }
  | { readonly kind: 'submitting' }
  | { readonly kind: 'exists'; readonly party: SelectableParty }
  | { readonly kind: 'error'; readonly detail: string };

const NAME_MIN = 2;
const NAME_MAX = 200;

function describeFailure(error: unknown): string {
  const kind = classifyApiError(error);
  if (kind === 'forbidden') return 'Your role cannot register a vendor — a contributor, Legal or an admin can.';
  if (error instanceof ApiError && error.status === 400) {
    return 'The API refused the name. Use the vendor’s legal name, 2 to 200 characters.';
  }
  if (error instanceof ApiError) return error.message;
  return 'The registration did not reach the API.';
}

export function RegisterVendorForm({
  id,
  disabled = false,
  initialName = '',
  onRegistered,
  onCancel,
}: RegisterVendorFormProps) {
  const [name, setName] = useState(initialName);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [fieldError, setFieldError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  useEffect(() => () => abortRef.current?.abort(), []);

  const submitting = phase.kind === 'submitting';
  const inputId = `${id}-vendor-name`;

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    /* Nested inside the surface's own form on some screens: this submit is ours alone. */
    event.stopPropagation();
    const trimmed = name.trim().replace(/\s+/g, ' ');
    if (trimmed.length < NAME_MIN || trimmed.length > NAME_MAX) {
      setFieldError(`Enter the vendor’s legal name — ${String(NAME_MIN)} to ${String(NAME_MAX)} characters.`);
      return;
    }
    setFieldError(null);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase({ kind: 'submitting' });
    void (async () => {
      try {
        const response = await registerVendorParty({ display_name: trimmed }, controller.signal);
        if (controller.signal.aborted) return;
        setPhase({ kind: 'idle' });
        onRegistered(response.party);
      } catch (error) {
        if (controller.signal.aborted) return;
        if (error instanceof ApiError && error.isUnauthenticated) {
          void msalInstance.loginRedirect(loginRequest);
          return;
        }
        const conflict = vendorRegistrationConflict(error);
        if (conflict?.code === 'vendor_exists') {
          setPhase({ kind: 'exists', party: conflict.party });
          return;
        }
        if (conflict?.code === 'vendor_key_space_exhausted') {
          setPhase({
            kind: 'error',
            detail: 'No free vendor key is left for a name starting this way. Ask an admin.',
          });
          return;
        }
        setPhase({ kind: 'error', detail: describeFailure(error) });
      }
    })();
  }

  return (
    <form className="rvf" aria-label="Register a new vendor" onSubmit={submit} aria-busy={submitting}>
      <div className="field grow">
        <label htmlFor={inputId}>Vendor legal name</label>
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          autoComplete="organization"
          spellCheck={false}
          maxLength={NAME_MAX}
          value={name}
          disabled={disabled || submitting}
          aria-invalid={fieldError === null ? undefined : true}
          aria-describedby={`${inputId}-hint`}
          onChange={(event) => {
            setName(event.target.value);
            if (phase.kind === 'exists' || phase.kind === 'error') setPhase({ kind: 'idle' });
          }}
        />
        <p id={`${inputId}-hint`} className="mut">
          The vendor key is minted by CLM from this name and shown once registered — it is never typed
          (FR-14). Registered here until the Dynamics sync exists (IR-03).
        </p>
      </div>
      <div className="actions">
        <button type="submit" className="btn sm pri" disabled={disabled || submitting}>
          Register
        </button>
        <button type="button" className="btn sm qt" disabled={submitting} onClick={onCancel}>
          Cancel
        </button>
      </div>
      {submitting ? (
        <p className="mut" role="status">
          Registering…
        </p>
      ) : null}
      {phase.kind === 'exists' ? (
        <div className="rvf-exists" role="status">
          <p>
            <strong>{phase.party.display_name}</strong> <span className="mut">({phase.party.party_key})</span>{' '}
            already carries this name. CLM registers each vendor once.
          </p>
          <button
            type="button"
            className="btn sm pri"
            onClick={() => {
              onRegistered(phase.party);
            }}
          >
            Use this vendor
          </button>
        </div>
      ) : null}
      {phase.kind === 'error' ? (
        <p className="ferr" role="alert">
          {phase.detail}
        </p>
      ) : null}
      {fieldError === null ? null : (
        <p className="ferr" role="alert">
          {fieldError}
        </p>
      )}
    </form>
  );
}
